/**
 * InboxView — faithful port of the canonical inbox screen.
 * 2-col: 380px message list | detail pane with suggested reply.
 *
 * UIR-05 round 2 (2026-07-25): reply / archive / mark-unread now call the real
 * inbox action ops (`replyInboxMessage` / `archiveInboxMessage` /
 * `markInboxUnread` — declared, typed and mock-served); the earlier pass had
 * disabled them as "no declared op yet" on a stale premise. The reply draft is
 * seeded from the API's own `suggestedReply` (was a hardcoded fabricated draft)
 * and the pill counts come from the list response's `unreadCount` /
 * `assignedToMeCount` ("Assigned to me · 3" was invented).
 * Still genuinely opless — disabled + declared, REQ-075: filter, assign,
 * snooze, close, mark-as-confirmed, schedule-send.
 */
import { useState, useEffect } from "react";
import { alpha, Avatar, Btn, Card, Icon, IconBtn, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { listInboxMessages, getInboxMessage, replyInboxMessage, archiveInboxMessage, markInboxUnread } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";


interface InboxMessage { id:string; from:string; email:string; subj:string; evt:string; channel:string; unread:boolean; archived:boolean; time:string; snippet:string; body:string; suggestedReply:string; init:string; color:string; tag:string; priority:string; }

const TAG_TONES: Record<string, "success"|"info"|"primary"|"neutral"|"warn"|"danger"> = {
  Confirm:"success", Question:"info", Press:"primary", Dietary:"neutral", Sponsor:"warn", Cancel:"danger", "Plus-one":"info", Bounce:"danger",
};

function ChannelIcon({ ch }: { ch: string }) {
  if (ch === "Email")  return <Icon.mail    size={10} />;
  if (ch === "SMS")    return <Icon.msg     size={10} />;
  if (ch === "Form")   return <Icon.browser size={10} />;
  return <Icon.warn size={10} />;
}

export default function InboxView() {
  const t = useT();
  const nav  = useNav();
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [counts, setCounts] = useState<{ unread:number; assigned:number }>({ unread:0, assigned:0 });
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const fetchMessages = async (): Promise<InboxMessage[]> => {
    const r: any = await listInboxMessages({});
    const arr = r.messages ?? r.data ?? [];
    setCounts({
      unread: typeof r.unreadCount === "number" ? r.unreadCount : arr.filter((m: any) => m.unread).length,
      assigned: typeof r.assignedToMeCount === "number" ? r.assignedToMeCount : 0,
    });
    return arr.map((m: any) => ({
      id: String(m.id ?? m.messageId ?? ""),
      from: m.fromName ?? m.from ?? "",
      email: m.fromEmail ?? m.email ?? "",
      subj: m.subject ?? m.subj ?? "",
      evt: m.eventTitle ?? m.evt ?? "",
      channel: m.channel ?? "Email",
      unread: !!m.unread,
      archived: !!m.archived,
      time: m.receivedAt ?? m.time ?? "",
      snippet: m.snippet ?? m.body?.slice(0,120) ?? "",
      body: m.body ?? "",
      suggestedReply: m.suggestedReply ?? "",
      init: (m.fromName ?? m.from ?? "?").slice(0,2).toUpperCase(),
      color: "#5142C7",
      tag: m.tag ?? m.priority ?? "",
      priority: m.priority ?? "medium",
    }));
  };
  useEffect(() => { fetchMessages().then(setMessages).catch(() => setMessages([])); }, []);
  const refresh = async () => {
    setRefreshing(true);
    try {
      setMessages(await fetchMessages());
      toast({ tone:"success", title:t("inbox.view.refreshed", "Refreshed") });
    } catch (e) {
      console.warn("refresh inbox failed:", e);
      toast({ tone:"danger", title:t("inbox.view.couldnTRefresh", "Couldn't refresh"), subtitle:t("inbox.view.retryInAMoment", "Retry in a moment.") });
    } finally { setRefreshing(false); }
  };
  const [active, setActive] = useState("");
  // Addendum item 2 residue: the detail pane renders the FETCHED record, not
  // the cached list row — getInboxMessage on selection merges the served body.
  useEffect(() => {
    const id = active || messages[0]?.id;
    if (!id) return;
    let live = true;
    getInboxMessage(id).then((r: any) => {
      if (!live) return;
      const d = r.message ?? r;
      if (!d || !d.id) return;
      setMessages((ms) => ms.map((m) => (m.id === String(d.id) ? { ...m, body: d.body ?? m.body, snippet: d.snippet ?? m.snippet, suggestedReply: d.suggestedReply ?? m.suggestedReply } : m)));
    }).catch((e: unknown) => console.warn("getInboxMessage failed:", e));
    return () => { live = false; };
  }, [active, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reply drafts are per message and seeded from the API's own `suggestedReply`
  // (UIR-05 round 2: the screen used to ship a hardcoded fabricated draft).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const comingSoon = t("inbox.view.comingSoonNoDeclaredOpYet", "Coming soon — no declared op yet");

  const cur      = (messages.length ? messages : []).find((m) => m.id === active) ?? messages[0];
  const unread   = counts.unread;
  const draft    = cur ? (drafts[cur.id] ?? cur.suggestedReply) : "";

  /** Apply one of the real inbox action ops, then reconcile from the server. */
  const runAction = async (key: string, fn: () => Promise<unknown>, okTitle: string, failTitle: string) => {
    setBusy(key);
    try {
      await fn();
      setMessages(await fetchMessages());
      toast({ tone:"success", title: okTitle });
    } catch (e) {
      console.warn(`${key} failed:`, e);
      toast({ tone:"danger", title: failTitle, subtitle: t("inbox.view.retryInAMoment", "Retry in a moment.") });
    } finally { setBusy(null); }
  };
  const sendReply = () => {
    if (!cur || !draft.trim()) return;
    void runAction("reply", async () => {
      await replyInboxMessage(cur.id, { replyBody: draft.trim() });
      setDrafts((d) => ({ ...d, [cur.id]: "" }));
    }, t("inbox.view.replySent", "Reply sent"), t("inbox.view.couldnTSendThatReply", "Couldn't send that reply"));
  };
  const archive = () => {
    if (!cur) return;
    void runAction("archive", () => archiveInboxMessage(cur.id), t("inbox.view.archived", "Archived"), t("inbox.view.couldnTArchiveThatMessage", "Couldn't archive that message"));
  };
  const markUnread = () => {
    if (!cur) return;
    void runAction("unread", () => markInboxUnread(cur.id), t("inbox.view.markedAsUnread", "Marked as unread"), t("inbox.view.couldnTMarkThatUnread", "Couldn't mark that unread"));
  };

  return (
    <Screen rail="inbox">
      <PageHeader
        title={t("inbox.view.inbox", "Inbox")}
        subtitle={i18nT("inbox.view.unreadUnreadRepliesBookingEnquiriesBouncesAndWebformSubmissionsAcrossAll.icu", { value0: unread })}
        actions={<>
          <Btn variant="secondary" icon={<Icon.filter size={14} />} disabled title={comingSoon}>{t("inbox.view.filter", "Filter")}</Btn>
          <Btn variant="secondary" icon={<Icon.users  size={14} />} disabled title={comingSoon}>{t("inbox.view.assign", "Assign")}</Btn>
          <Btn variant="secondary" icon={<Icon.refresh size={14} />} onClick={refresh} disabled={refreshing}>{t("common.actions.refresh", "Refresh")}</Btn>
        </>}
      />
      <div style={{ flex:1, display:"grid", gridTemplateColumns:"380px 1fr", gap:0, minHeight:0, borderTop:`1px solid ${T.border}` }}>
        {/* Left: message list */}
        <div style={{ borderRight:`1px solid ${T.border}`, background:T.white, overflow:"auto", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"10px 14px", borderBottom:`1px solid ${T.border}` }}>
            <Input full leading={<Icon.search size={14} />} placeholder={t("inbox.view.searchMessages", "Search messages…")} size="sm" />
          </div>
          <div style={{ display:"flex", gap:4, padding:"8px 14px", borderBottom:`1px solid ${T.border}`, flexWrap:"wrap" }}>
            <Pill tone="primary">{t("inbox.view.all", "All ·")}{messages.length}</Pill>
            <Pill>{t("inbox.view.unread", "Unread ·")}{unread}</Pill>
            <Pill>{t("inbox.view.assignedToMe", "Assigned to me ·")}{counts.assigned}</Pill>
          </div>
          <ul style={{ listStyle:"none", margin:0, padding:0, flex:1, overflow:"auto" }}>
            {messages.map((m) => (
              <li key={m.id} onClick={() => setActive(m.id)} style={{
                padding:"12px 14px", borderBottom:`1px solid ${T.border}`,
                background: active === m.id ? T.p4 : m.unread ? T.white : T.n6,
                borderLeft: active === m.id ? `3px solid ${T.p1}` : "3px solid transparent",
                cursor:"pointer", display:"flex", gap:10,
              }}>
                <Avatar size={32} initials={m.init} color={m.color} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:6 }}>
                    <span style={{ fontSize:13, color:T.n1, fontWeight:m.unread?700:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.from}</span>
                    <span style={{ fontSize:11, color:m.unread?T.p1:T.soft, flexShrink:0, fontWeight:m.unread?600:400 }}>{m.time}</span>
                  </div>
                  <div style={{ fontSize:12, color:T.n1, fontWeight:m.unread?600:500, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.subj}</div>
                  <div style={{ fontSize:11, color:T.soft, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.snippet}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:6 }}>
                    <Pill tone={TAG_TONES[m.tag]??'neutral'}>{m.tag}</Pill>
                    <span style={{ fontSize:10, color:T.soft, display:"inline-flex", alignItems:"center", gap:4 }}>
                      <ChannelIcon ch={m.channel} />{m.evt}
                    </span>
                    {m.unread ? <span style={{ marginLeft:"auto", width:6, height:6, borderRadius:999, background:T.p1 }} /> : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: detail */}
        {!cur ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", background:T.white, padding:40, textAlign:"center" }}>
            <div>
              <div style={{ fontSize:14, fontWeight:600, color:T.n2 }}>{t("inbox.view.noMessages", "No messages")}</div>
              <div style={{ fontSize:12.5, color:T.soft, marginTop:4 }}>{t("inbox.view.nothingInTheInboxRightNow", "Nothing in the inbox right now.")}</div>
            </div>
          </div>
        ) : (
        <div style={{ display:"flex", flexDirection:"column", background:T.white, minWidth:0, overflow:"hidden" }}>
          <div style={{ padding:"16px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
            <div style={{ display:"flex", gap:12, alignItems:"center", minWidth:0 }}>
              <Avatar size={40} initials={cur.init} color={cur.color} />
              <div style={{ minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <h2 style={{ fontSize:16, fontWeight:700, color:T.n1, margin:0 }}>{cur.subj}</h2>
                  <Pill tone={TAG_TONES[cur.tag]??'neutral'}>{cur.tag}</Pill>
                </div>
                <div style={{ fontSize:12, color:T.soft, marginTop:2 }}>
                  {cur.from} {i18nT("inbox.view.lt.rendered", "&lt;")}{cur.email}{i18nT("inbox.view.gt.rendered", "&gt; ·")} {cur.evt} · {cur.channel} · {cur.time}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:4, flexShrink:0 }}>
              <IconBtn label={t("inbox.view.markUnread", "Mark unread")} onClick={markUnread} disabled={busy !== null}><Icon.mail   size={14} /></IconBtn>
              <IconBtn label={t("inbox.view.assign", "Assign")}       disabled title={comingSoon}><Icon.users  size={14} /></IconBtn>
              <IconBtn label={t("inbox.view.snooze", "Snooze")}       disabled title={comingSoon}><Icon.clock  size={14} /></IconBtn>
              <IconBtn label={t("inbox.view.archive", "Archive")} onClick={archive} disabled={busy !== null}><Icon.trash  size={14} /></IconBtn>
              <IconBtn label={t("common.actions.close", "Close")}        disabled title={comingSoon}><Icon.x      size={14} /></IconBtn>
            </div>
          </div>

          <div style={{ flex:1, overflow:"auto", padding:24, display:"flex", flexDirection:"column", gap:16 }}>
            {/* Message body */}
            <Card style={{ padding:20, background:T.n6 }}>
              <div style={{ fontSize:14, color:T.n1, lineHeight:1.6, whiteSpace:"pre-wrap" }}>{cur.body || cur.snippet}</div>
            </Card>

            {/* Quick actions */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              <Btn variant="secondary" icon={<Icon.check  size={14} />} disabled title={comingSoon}>{t("inbox.view.markAsConfirmed", "Mark as confirmed")}</Btn>
              <Btn variant="secondary" icon={<Icon.user   size={14} />} onClick={() => nav.go("guests.profile", { id:"g1" })}>{t("inbox.view.openGuestProfile", "Open guest profile")}</Btn>
              <Btn variant="secondary" icon={<Icon.calendar size={14} />} onClick={() => nav.go("events")}>{t("inbox.view.openEvent", "Open event")}</Btn>
            </div>

            {/* Suggested reply */}
            <Card>
              <div style={{ padding:"10px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <Icon.star size={12} style={{ color:T.p1 }} />
                  <span style={{ fontSize:13, color:T.n1, fontWeight:600 }}>{t("inbox.view.suggestedReply", "Suggested reply")}</span>
                </div>
                <Btn variant="ghost" size="sm" onClick={() => setDrafts((d) => ({ ...d, [cur.id]: "" }))}>{t("common.actions.clear", "Clear")}</Btn>
              </div>
              <div style={{ padding:16, display:"flex", flexDirection:"column", gap:10 }}>
                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cur.id]: e.target.value }))}
                  rows={7}
                  style={{ width:"100%", resize:"vertical", border:`1px solid ${T.n4}`, borderRadius:8, padding:"10px 12px", fontFamily:T.font, fontSize:13, color:T.n1, outline:"none", boxSizing:"border-box", lineHeight:1.6 }}
                />
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                  <Btn variant="secondary" disabled title={comingSoon}>{t("inbox.view.schedule", "Schedule")}</Btn>
                  <Btn icon={<Icon.send size={14} />} onClick={sendReply} disabled={!draft.trim() || busy !== null}>
                    {busy === "reply" ? t("inbox.view.sending", "Sending…") : t("inbox.view.sendReply", "Send reply")}</Btn>
                </div>
              </div>
            </Card>
          </div>
        </div>
        )}
      </div>
    </Screen>
  );
}
